import { getFeatureFlag, flagsAreReady } from '@fs/zion-flags'

const staleThreshold = 15 * 60 * 1000 // 15 minutes

/**
 * Fetches the current user's content cards from Braze.
 *
 * Content cards are cached for 15 minutes by default, but `forceRefresh` can be set to true to bypass the cache.
 * Content cards should have a `type` key/value pair in the `extras` object to filter them by type.
 * Filters the cards by the specified `type` key value pair in the content card settings (default is 'notification').
 *
 * @param {Object} [options={}]
 * @param {boolean} [options.forceRefresh=false] - Whether to force a refresh of the content cards.
 * @param {string} [options.type='notification'] - The type of content cards to filter by, such as 'mbox' or 'notification'.
 * @returns {Promise<Array>} A promise that resolves to an array of filtered content cards.
 */
export async function getContentCards({ forceRefresh = false, type = 'notification' } = {}) {
  try {
    await waitForBrazeInitialization()
    const { getCachedContentCards, requestContentCardsRefresh } = await import('@fs/zion-braze')
    let contentCardsObject = getCachedContentCards()
    const areCardsStale =
      forceRefresh || (contentCardsObject?.lastUpdated?.getTime() || 0) < Date.now() - staleThreshold

    if (areCardsStale) {
      const refreshPromise = new Promise((resolve, reject) => {
        requestContentCardsRefresh(resolve, reject)
      })
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Content cards refresh timed out')), 5000)
      })
      await Promise.race([refreshPromise, timeoutPromise])
      contentCardsObject = getCachedContentCards()
    }
    // The `type` key value pair must be added to each content card in Braze to specify its intended use
    const filteredContentCards = contentCardsObject.cards.filter((card) => !type || card.extras?.type === type)
    return filteredContentCards
  } catch (error) {
    return []
  }
}

/**
 * Determines whether content cards should be loaded based on feature flags.
 * @returns {Promise<boolean>}
 */
export async function getShouldLoadContentCards() {
  await flagsAreReady()
  const brazeFlag = getFeatureFlag('frontier_braze')
  return brazeFlag.isOn
}

/**
 * The Braze SDK is lazily initialized in zion-root, and calls to it will fail until it is initialized.
 * This function waits for the Braze SDK to be initialized by checking for the `window.fsBraze` global variable.
 *
 * @param {number} [timeout=5000] - The maximum time to wait for initialization, in milliseconds.
 * @param {number} [interval=100] - The interval between checks, in milliseconds.
 * @returns {Promise<boolean>} Resolves to `true` if Braze is initialized before the timeout, otherwise rejects with an error.
 */
export async function waitForBrazeInitialization(timeout = 5000, interval = 100) {
  const start = Date.now()
  return new Promise((resolve, reject) => {
    const check = () => {
      if (typeof window !== 'undefined' && typeof window.fsBraze === 'object') {
        resolve(true)
      } else if (Date.now() - start >= timeout) {
        reject(new Error('Timed out waiting for Braze initialization'))
      } else {
        setTimeout(check, interval)
      }
    }
    check()
  })
}

/**
 * Retrieves the timestamp (in milliseconds since epoch) of the latest unread
 * and undismissed notification content card, or null if there are no such cards.
 * @returns {Promise<number|null>}
 */
export async function getLastUnreadContentCardTimestamp() {
  try {
    const contentCards = await getContentCards()
    if (contentCards.length === 0) {
      return null
    }
    const unreadContentCards = contentCards.filter((card) => !card.viewed && !card.dismissed)
    const latest = unreadContentCards
      .map((card) => (card.updated instanceof Date ? card.updated.getTime() : null))
      .filter((date) => date !== null)
      .sort((a, b) => b - a)[0]
    return latest || null
  } catch (error) {
    return null
  }
}

const getContentCardThreadState = (card) => {
  if (card?.dismissed) return 'TRASH'
  if (card?.viewed) return 'READ'
  return 'UNREAD'
}

/** Wraps a Braze content card notification in a thread-like object for use alongside traditional notification threads */
export const contentCardToThread = (card) => ({
  threadId: `content-card-${card.id}`,
  about: card.title || card.description || '',
  // If description was already used as about, don’t repeat it in subject
  subject: (card.title ? card.description : '') || '',
  lastModifiedTime: card.updated || card.created || new Date().toISOString(),
  userThreadState: getContentCardThreadState(card),
  isContentCard: true,
  contentCard: card,
})
